<template>
  <div class="vz-individual-order-additional pdf-order-section">
    <div class="text-h1 color-light mb-10">
      Доп. услуги
    </div>
    <div class="vz-personal-order-blocks tiny-gap">
      <div class="vz-individual-order-additional-part">
        <div v-if="disabled" class="disabled-card" :title="disabledByCondition" />
        <vz-checkbox
          v-model="scan"
          :disabled="disabled"
          big
          label="Скан накладной на выдачу"
        />
        <vz-checkbox-collapse
          v-if="!hideDisassemble"
          v-model="disassemble"
          :disabled="disabled"
          collapsible
          big
          class="mt-20"
          label="Разбор упаковки при доставке до адреса"
        >
          <div class="flex flex-align-items-end">
            <vz-measure-input
              v-model="disassembleWeight"
              label="Вес"
              class="small-width"
              placeholder="1"
              measure="кг"
              :max-value="19999"
              :min-value="0.1"
              @change="orderStore.getOrderPriceAction"
            />
            <div class="color-light text-caption vz-individual-order-additional-caption">
              Максимальный вес одного места после разбора упаковки
            </div>
          </div>
        </vz-checkbox-collapse>
      </div>
      <div class="vz-individual-order-additional-part">
        <div v-if="orderLockDispatch && !isCloned" class="disabled-card" :title="disabledByCondition" />
        <vz-checkbox-collapse
          v-model="returning"
          :disabled="orderLockDispatch && !isCloned"
          big
          collapsible
          label="Вернуть сопроводительные документы"
        >
          <vz-direction
            v-if="returning"
            v-model:location-guid="orderStore.orderForm.additionalServices.returning.locationGuid"
            v-model:direction="orderStore.orderForm.additionalServices.returning.direction"
            v-model:terminal-guid="orderStore.orderForm.additionalServices.returning.terminalGuid"
            v-model:address="orderStore.orderForm.additionalServices.returning.address"
            :disabled="orderLockDispatch && !isCloned"
            :country="country"
            is-request-docs
            use-map
            terminal-purpose="returnOfSupportingDocuments"
            selector-title="Населённый пункт"
            class="mt-10"
            @update:direction="onUpdateDirection"
          />
          <vz-checkbox
            v-model="scanDocuments"
            :disabled="orderLockDispatch && !isCloned"
            class="mt-20"
            label="Сканировать"
          />
        </vz-checkbox-collapse>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useOrderStore} from '~/stores/order';
import VzDirection from '~/components/personal/order/direction/vz-direction.vue';
import {until} from '@vueuse/shared';
import {OrderDirectionType} from '~/types/types';

const props = defineProps({
  hideDisassemble: {
    type: Boolean,
    default: false
  }
})

const orderStore = useOrderStore()

const scan = computed({
  get(){
    return orderStore.orderForm.additionalServices.scan
  },
  set(data: boolean) {
    orderStore.orderForm.additionalServices.scan = data
    orderStore.getOrderPriceAction()
  }
})

const disassemble = computed({
  get(){
    return orderStore.orderForm.additionalServices.disassemble.active
  },
  set(data: boolean) {
    orderStore.orderForm.additionalServices.disassemble.active = data
    if (data) {
      disassembleWeight.value = orderStore.orderForm.cargo.dimensions.general.weight
    }
    orderStore.getOrderPriceAction()
  }
})

const disassembleWeight = computed({
  get(){
    return orderStore.orderForm.additionalServices.disassemble.weight
  },
  set(data: number) {
    orderStore.orderForm.additionalServices.disassemble.weight = data
  }
})

const returning = computed({
  get(){
    return orderStore.orderForm.additionalServices.returning.active
  },
  set(data: boolean) {
    orderStore.orderForm.additionalServices.returning.active = data
    if (data) {
      orderStore.orderForm.additionalServices.returning.locationGuid =  orderStore.orderForm.directions.dispatch.locationGuid
    }
  }
})

const scanDocuments = computed({
  get(){
    return orderStore.orderForm.additionalServices.scanDocuments
  },
  set(data: boolean) {
    orderStore.orderForm.additionalServices.scanDocuments = data
    orderStore.getOrderPriceAction()
  }
})

const isCloned = computed(() => {
  return !!useRoute().query.clone || false
})

const disabledByCondition = computed(() => {
  if (orderStore.orderConditionValues?.additionalServices?.disabled) {
    return orderStore.orderConditionValues?.additionalServices?.reason
  }
  return false
})

const orderLockDispatch = computed(() => {
  return Boolean(orderStore.orderForm?.meta?.lockState?.dispatch) || Boolean(disabledByCondition.value)
})

const orderLockDestination = computed(() => {
  return Boolean(orderStore.orderForm?.meta?.lockState?.destination) || Boolean(disabledByCondition.value)
})

const disabled = computed(() => {
  return orderLockDestination.value && !isCloned.value
})

const country = computed(() => {
  return Array.from(
    new Set([orderStore.orderForm.directions.dispatch.country, orderStore.orderForm.directions.destination.country])
  ).filter(v => !!v)
})

async function onUpdateDirection(v: OrderDirectionType) {
  if (v !== 'address') {
    await until(() => !!orderStore.orderForm.additionalServices.returning.terminalGuid).toBe(true)
  }
  orderStore.getOrderPriceAction()
}

watch(
  () => orderStore.orderForm.additionalServices.returning,
  (oldValue, newValue) => {
    if (oldValue?.active !== newValue.active) {
      orderStore.getOrderPriceAction()
    }
  },
  { deep: true }
)

watch(
  () => [orderStore.orderForm.directions.dispatch.locationGuid, orderStore.orderForm.directions.destination.country],
  () => {
    if (orderStore.orderForm.additionalServices.returning.direction === 'address') {
      orderStore.orderForm.additionalServices.returning.address = ''
    }
  },
)

watch(disabledByCondition, () => {
  if (!disabledByCondition.value) {
    return
  }
  // Присвоение не через сеттер, чтобы price не запрашивался повторно
  if (scan.value) {
    orderStore.orderForm.additionalServices.scan = false
  }
  if (disassemble.value) {
    orderStore.orderForm.additionalServices.disassemble.active = false
  }
  if (returning.value) {
    orderStore.orderForm.additionalServices.returning.active = false
  }
})
</script>
